import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { captureError, featureFlagsMap, hasActiveFeatureFlag, platformSelectors } from '@clatter/platform';

import { useAppSelector } from '../../../store';
import { routesNames } from '../../../routes';
import {
  useCreateProjectMutation,
  useCreatePresentationMutation,
  useGetProjectTemplatesCardsQuery,
  useSearchQuery,
} from '../../../services';
import { FolderManagerItemTypes } from '../../../interfaces';
import { buildSlideKey } from '../../../helpers';
import { HomeSearchBar } from '../.././../pages/Home/components/HomeSearchBar';
import {
  addNotification,
  assetManagerUISelectors,
  authSelectors,
  pinItem,
  unpinItem,
  pinnedItemsSelectors,
} from '../../../store';
import { config } from '../../../config';

const PRIORITY_TEMPLATE_NAMES = ['Coley Insurance Proposal Demo', 'Insurance Proposal', 'Specialty Benefits'];
const MAX_SEARCH_ACTIONS = 4;

const TYPE_ORDER: FolderManagerItemTypes[] = [
  FolderManagerItemTypes.PRESENTATION,
  FolderManagerItemTypes.WORKSPACE,
  FolderManagerItemTypes.TEMPLATE,
  FolderManagerItemTypes.SLIDE_REPOSITORY,
  FolderManagerItemTypes.FOLDER,
  FolderManagerItemTypes.SLIDE,
  FolderManagerItemTypes.ASSET,
];

const TYPE_LABELS: Partial<Record<FolderManagerItemTypes, string>> = {
  [FolderManagerItemTypes.PRESENTATION]: 'Presentation',
  [FolderManagerItemTypes.WORKSPACE]: 'Workspace',
  [FolderManagerItemTypes.TEMPLATE]: 'Template',
  [FolderManagerItemTypes.SLIDE_REPOSITORY]: 'Slidespace',
  [FolderManagerItemTypes.FOLDER]: 'Folder',
  [FolderManagerItemTypes.SLIDE]: 'Slide',
  [FolderManagerItemTypes.ASSET]: 'File',
};

const FILE_TYPE_LABELS: Record<string, string> = {
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'application/pdf': 'PDF',
};

const SEARCH_TYPE_MAP: Partial<Record<string, string>> = {
  '': 'all',
  [FolderManagerItemTypes.WORKSPACE]: 'projects',
  [FolderManagerItemTypes.TEMPLATE]: 'projects',
  [FolderManagerItemTypes.SLIDE_REPOSITORY]: 'slide_repositories',
  [FolderManagerItemTypes.SLIDE]: 'slides',
  [FolderManagerItemTypes.FOLDER]: 'folders',
  [FolderManagerItemTypes.ASSET]: 'files',
};

// Maps API result type strings to FolderManagerItemTypes
const API_RESULT_TYPE_MAP: Record<string, FolderManagerItemTypes> = {
  file: FolderManagerItemTypes.ASSET,
  folder: FolderManagerItemTypes.FOLDER,
  slide: FolderManagerItemTypes.SLIDE,
  project: FolderManagerItemTypes.WORKSPACE,
  slide_repository: FolderManagerItemTypes.SLIDE_REPOSITORY,
};

export const NavSearchBar: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const featureFlags = useAppSelector(platformSelectors.featureFlag.selectAllFeatureFlags);
  const isSlidespacesEnabled = hasActiveFeatureFlag({
    flagName: featureFlagsMap.NG.slidespaces,
    featureFlags: featureFlags,
  });

  const isWorkspacesEnabled = hasActiveFeatureFlag({
    flagName: featureFlagsMap.NG.workspaces,
    featureFlags: featureFlags,
  });

  const myAssetsFolder = useAppSelector((state) => assetManagerUISelectors.getSidebarItemBySlug(state, 'my-assets'));
  const activeUserId = useAppSelector(authSelectors.getUserAuthState)?.user?.sub;

  const { data: projectTemplatesData } = useGetProjectTemplatesCardsQuery();

  const [loadingTemplateId, setLoadingTemplateId] = useState<number | null>(null);
  const [liveQuery, setLiveQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [activeFilterType, setActiveFilterType] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(liveQuery), config.defaultDebounce);
    return () => clearTimeout(timer);
  }, [liveQuery]);

  const searchApiType = SEARCH_TYPE_MAP[activeFilterType] ?? 'all';
  const { data: searchData, isFetching: isSearchFetching } = useSearchQuery(
    { q: debouncedQuery, type: searchApiType },
    { skip: !debouncedQuery },
  );

  const [createProject] = useCreateProjectMutation();
  const [createPresentation] = useCreatePresentationMutation();

  const createPresentationFromRepository = useCallback(
    async (slideRepositoryId: number) => {
      if (!myAssetsFolder?.id) {
        dispatch(addNotification({ type: 'error', message: 'Couldn\'t create the presentation — the destination folder wasn\'t found.' }));
        return;
      }
      try {
        const created = await createPresentation({
          displayName: 'New Presentation',
          folderId: myAssetsFolder.id,
          slideRepositoryId,
        }).unwrap();
        navigate(generatePath(routesNames.presentationsEdit, { presentationId: created.id }));
      } catch (error) {
        captureError(error as Error, { component: 'NavSearchBar' });
        dispatch(addNotification({ type: 'error', message: 'Couldn\'t create the presentation. Please try again.' }));
      }
    },
    [myAssetsFolder, createPresentation, navigate, dispatch],
  );

  const handlePinSearchItem = useCallback(
    (item: HomeSearchItem) => {
      if (!activeUserId) return;
      const isPinned = item.isPinned;
      if (isPinned) {
        dispatch(unpinItem({ userId: activeUserId, itemId: String(item.id) }));
        dispatch(addNotification({ type: 'success', message: 'Item unpinned.' }));
      } else {
        dispatch(pinItem({ userId: activeUserId, item: { id: String(item.id), name: item.name, type: item.type, folderId: item.folderId, selectId: item.selectId, isTrashed: item.isTrashed } }));
        dispatch(addNotification({ type: 'success', message: 'Item pinned to Home.' }));
      }
    },
    [activeUserId, dispatch],
  );

  const handleSearchItemSelect = useCallback(
    (item: HomeSearchItem) => {
      switch (item.type) {
        case FolderManagerItemTypes.WORKSPACE:
          if (item.id) {
            navigate(generatePath(routesNames.editProject, { projectId: item.id }));
          } else {
            navigate(routesNames.myWorkspace());
          }
          break;
        case FolderManagerItemTypes.SLIDE_REPOSITORY:
          if (item.id) {
            createPresentationFromRepository(Number(item.id));
          }
          break;
        case FolderManagerItemTypes.PRESENTATION:
          navigate(routesNames.assets('company-assets'));
          break;
        case FolderManagerItemTypes.FOLDER:
          if (item.folderId) {
            const folderSlug = item.isTrashed ? 'trash' : 'company-assets';
            const folderPath = routesNames.folders(folderSlug, String(item.folderId));
            navigate(item.selectId != null ? `${folderPath}?selected=${item.selectId}` : folderPath);
          } else {
            navigate(routesNames.assets(item.isTrashed ? 'trash' : 'my-assets'));
          }
          break;
        case FolderManagerItemTypes.ASSET:
          if (item.folderId) {
            const assetSlug = item.isTrashed ? 'trash' : 'company-assets';
            const assetPath = routesNames.folders(assetSlug, String(item.folderId));
            navigate(item.selectId != null ? `${assetPath}?selected=${item.selectId}` : assetPath);
          }
          break;
        case FolderManagerItemTypes.SLIDE:
          if (item.folderId) {
            const slideSlug = item.isTrashed ? 'trash' : 'company-assets';
            const slidePath = routesNames.folders(slideSlug, String(item.folderId));
            navigate(item.selectId != null ? `${slidePath}?selected=${item.selectId}` : slidePath);
          }
          break;
        default:
          break;
      }
    },
    [navigate, createPresentationFromRepository],
  );

  const createWorkspaceFromTemplate = useCallback(
    async (templateId: number) => {
      setLoadingTemplateId(templateId);
      try {
        const created = await createProject({ projectTemplateId: templateId }).unwrap();
        if (created?.id) {
          navigate(generatePath(routesNames.editProject, { projectId: created.id }));
        }
      } catch (error) {
        captureError(error as Error, { component: 'NavSearchBar' });
      } finally {
        setLoadingTemplateId(null);
      }
    },
    [createProject, navigate],
  );

  const handleSearchActionSelect = useCallback(
    async (action: HomeSearchAction) => {
      if (!action.templateId) return;
      await createWorkspaceFromTemplate(action.templateId);
    },
    [createWorkspaceFromTemplate],
  );

  const searchActions = useMemo<HomeSearchAction[]>(() => {
    const published = (projectTemplatesData || []).filter((template) => template.status === 'published');
    const byName = new Map(published.map((template) => [template.displayName, template]));
    const prioritySet = new Set(PRIORITY_TEMPLATE_NAMES);
    const candidates = [
      ...(PRIORITY_TEMPLATE_NAMES.map((name) => byName.get(name)).filter(Boolean) as typeof published),
      ...published
        .filter((template) => !prioritySet.has(template.displayName))
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()),
    ];
    return candidates.slice(0, MAX_SEARCH_ACTIONS).map((template) => ({
      label: `New ${template.displayName}`,
      templateId: template.id,
      isLoading: loadingTemplateId === template.id,
      onSelect: handleSearchActionSelect,
    }));
  }, [projectTemplatesData, loadingTemplateId, handleSearchActionSelect]);

  const allPinnedItems = useAppSelector((state) =>
    activeUserId ? pinnedItemsSelectors.forUser(state, activeUserId) : undefined,
  );

  const apiSearchItems = useMemo<HomeSearchItem[]>(() => {
    if (!searchData?.data) return [];
    return searchData.data.map((result, idx) => {
      const itemType = API_RESULT_TYPE_MAP[result.type] ?? FolderManagerItemTypes.ASSET;
      const isSlide = itemType === FolderManagerItemTypes.SLIDE;
      const isFolder = itemType === FolderManagerItemTypes.FOLDER;

      const assetData = isSlide ? (result.data as any)?.asset : null;

      const sub = isSlide
        ? (assetData?.DisplayName ?? FILE_TYPE_LABELS[assetData?.Type] ?? 'Slide')
        : itemType === FolderManagerItemTypes.ASSET
        ? (FILE_TYPE_LABELS[result.data?.Type] ?? result.data?.Type ?? 'File')
        : TYPE_LABELS[itemType] ?? result.type;

      const folderId = isFolder
        ? (result.data as any)?.ParentFolderID
        : isSlide
        ? assetData?.FolderID
        : result.data?.FolderID;

      const isTrashed = isSlide
        ? !!assetData?.TrashedAt
        : !!(result.data as any)?.TrashedAt;

      const slideId = result.data.ID;
      const selectId: string | undefined = isSlide
        ? (slideId != null ? buildSlideKey(slideId) : undefined)
        : result.id ? String(result.id) : undefined;

      const itemId = result.id ? String(result.id) : `${result.type}-${idx}`;
      const isPinned = allPinnedItems?.some((pinned) => pinned.id === itemId) ?? false;
      return {
        id: itemId,
        name: result.name,
        type: itemType,
        sub,
        folderId,
        isPinned,
        isTrashed,
        selectId,
        onPin: handlePinSearchItem,
        onSelect: handleSearchItemSelect,
      };
    });
  }, [searchData, allPinnedItems, handlePinSearchItem, handleSearchItemSelect]);

  const filterOptions = useMemo(() => {
    const active = [
      { value: FolderManagerItemTypes.SLIDE, label: 'Slides' },
      ...(isWorkspacesEnabled
        ? [
            { value: FolderManagerItemTypes.WORKSPACE, label: 'Workspaces' },
            { value: FolderManagerItemTypes.TEMPLATE, label: 'Templates' },
          ]
        : []),
      { value: FolderManagerItemTypes.FOLDER, label: 'Folders' },
      { value: FolderManagerItemTypes.ASSET, label: 'Files' },
      ...(isSlidespacesEnabled ? [{ value: FolderManagerItemTypes.SLIDE_REPOSITORY, label: 'Slidespaces' }] : []),
    ].sort(
      (a, b) =>
        TYPE_ORDER.indexOf(a.value as FolderManagerItemTypes) - TYPE_ORDER.indexOf(b.value as FolderManagerItemTypes),
    );

    return [{ value: '', label: 'All' }, ...active];
  }, [isSlidespacesEnabled, isWorkspacesEnabled]);

  const displayItems = liveQuery ? apiSearchItems : [];

  return (
    <HomeSearchBar
      searchButton
      items={displayItems}
      filterOptions={filterOptions}
      typeOrder={TYPE_ORDER}
      typeLabels={TYPE_LABELS}
      onSearch={setLiveQuery}
      onFilterChange={setActiveFilterType}
      isLoading={isSearchFetching}
    />
  );
};
